/**
 * Interactive Human Body Visualization — Overall Health Snapshot.
 *
 * Design principles:
 *   • Anatomically-informed male/female SVG silhouettes with layered detail.
 *   • Auto-selects gender from `patient_meta.gender` (override toggle available).
 *   • Every organ system has an anatomically-correct hotspot with a pulsing
 *     "aura" whose color reflects the worst status among its biomarkers.
 *   • Hover a hotspot → floating chip with the organ icon + label + tests count.
 *   • Click a hotspot → the right-side "Vitals panel" reveals system-specific
 *     data: gradient hero, health-ring, biomarker cards with in-range bars,
 *     sparkline trends, AI micro-summary, and follow-up test chips.
 *   • Legend chips at the bottom explain colors.
 *   • All animations are CSS-only (keyframes + transitions) — no external libs.
 */
import React, { useMemo, useState } from "react";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { statusColor, parseRange, rangePosition } from "@/lib/health";
import { Button } from "@/components/ui/button";
import { X, ChevronRight, TrendingUp, Info, Sparkles, ArrowDown } from "lucide-react";
import { BodySilhouetteAnatomical } from "./BodySilhouetteAnatomical";
import { useSiteSettings } from "@/lib/settings";

/* Biomarker → organ system mapping. Keys are lowercased alphanumeric-only. */
const SYSTEM_MAP = {
  brain: {
    label: "Brain & Nerves",
    icon: "🧠",
    gradientCss: "linear-gradient(135deg, #6366F1, #7C3AED)",
    ring: "#8B5CF6",
    keywords: ["homocystein", "vitaminb12", "folate", "brain", "neuron"],
    xy: { male: [50, 8], female: [50, 8] },
  },
  thyroid: {
    label: "Thyroid",
    icon: "🦋",
    gradientCss: "linear-gradient(135deg, #06B6D4, #14B8A6)",
    ring: "#06B6D4",
    keywords: ["tsh", "t3", "t4", "ft3", "ft4", "thyroid", "antitpo", "antitg"],
    xy: { male: [50, 19], female: [50, 19] },
  },
  heart: {
    label: "Heart",
    icon: "❤️",
    gradientCss: "linear-gradient(135deg, #F43F5E, #DC2626)",
    ring: "#EF4444",
    keywords: [
      "cholesterol", "ldl", "hdl", "triglycerid", "vldl", "nonhdl",
      "apoa", "apob", "lpa", "homocystein", "troponin", "ckmb", "bnp", "cardiac",
    ],
    xy: { male: [46, 30], female: [46, 30] },
  },
  blood: {
    label: "Blood",
    icon: "🩸",
    gradientCss: "linear-gradient(135deg, #EF4444, #B91C1C)",
    ring: "#DC2626",
    keywords: [
      "hemoglobin", "hb", "rbc", "wbc", "platelet", "mcv", "mch", "mchc",
      "hematocrit", "hct", "neutrophil", "lymphocyte", "monocyte", "eosinophil",
      "basophil", "esr", "reticulocyte", "cbc",
    ],
    xy: { male: [56, 30], female: [56, 30] },
  },
  liver: {
    label: "Liver",
    icon: "🫁",
    gradientCss: "linear-gradient(135deg, #F59E0B, #EA580C)",
    ring: "#F59E0B",
    keywords: ["alt", "ast", "sgpt", "sgot", "bilirubin", "ggt", "alkaline", "alp", "albumin", "globulin", "protein", "liver"],
    xy: { male: [42, 39], female: [42, 39] },
  },
  immune: {
    label: "Immunity",
    icon: "🛡",
    gradientCss: "linear-gradient(135deg, #D946EF, #BE185D)",
    ring: "#D946EF",
    keywords: ["vitaminc", "vitamine", "vitamina", "zinc", "iron", "ferritin", "tibc", "transferrin", "immunoglobulin", "ige", "crp"],
    xy: { male: [60, 34], female: [60, 34] },
  },
  pancreas: {
    label: "Pancreas",
    icon: "🍯",
    gradientCss: "linear-gradient(135deg, #EAB308, #CA8A04)",
    ring: "#EAB308",
    keywords: ["glucose", "hba1c", "insulin", "cpeptide", "diabetes", "fructosamine", "ogtt", "amylase", "lipase"],
    xy: { male: [58, 43], female: [58, 43] },
  },
  kidney: {
    label: "Kidney",
    icon: "🫘",
    gradientCss: "linear-gradient(135deg, #8B5CF6, #6D28D9)",
    ring: "#7C3AED",
    keywords: ["creatinin", "urea", "bun", "uricacid", "egfr", "cystatin", "microalbumin", "kidney"],
    xy: { male: [40, 48], female: [40, 48] },
  },
  digestive: {
    label: "Digestive",
    icon: "🥗",
    gradientCss: "linear-gradient(135deg, #10B981, #059669)",
    ring: "#10B981",
    keywords: ["stool", "occult", "gastric", "helicobacter", "hpylori", "digestive", "gut", "colon"],
    xy: { male: [50, 52], female: [50, 52] },
  },
  reproductive: {
    label: "Reproductive",
    icon: "⚧",
    gradientCss: "linear-gradient(135deg, #EC4899, #BE185D)",
    ring: "#EC4899",
    keywords: ["testosterone", "estradiol", "estrogen", "progesterone", "fsh", "lh", "prolactin", "amh", "dhea", "hcg", "psa"],
    xy: { male: [50, 60], female: [50, 60] },
  },
  bones: {
    label: "Bones & Minerals",
    icon: "🦴",
    gradientCss: "linear-gradient(135deg, #64748B, #334155)",
    ring: "#64748B",
    keywords: ["calcium", "phosphorus", "magnesium", "vitaminda", "vitamind", "25oh", "parathyroid", "pth", "bone"],
    xy: { male: [42, 78], female: [42, 78] },
  },
};

function normalize(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function classifyBiomarker(name) {
  const n = normalize(name);
  for (const [key, sys] of Object.entries(SYSTEM_MAP)) {
    if (sys.keywords.some((kw) => n.includes(kw))) return key;
  }
  return null;
}

function groupBySystem(biomarkers) {
  const groups = {};
  for (const b of biomarkers || []) {
    const sys = classifyBiomarker(b.name);
    if (!sys) continue;
    if (!groups[sys]) groups[sys] = [];
    groups[sys].push(b);
  }
  return groups;
}

function worstStatus(items) {
  const order = { concern: 5, high: 4, low: 3, borderline: 2, normal: 1 };
  let worst = "normal";
  for (const b of items) {
    if ((order[b.status] || 0) > (order[worst] || 0)) worst = b.status;
  }
  return worst;
}

function recommendedTests(systemKey, smart) {
  const sys = SYSTEM_MAP[systemKey];
  if (!sys) return [];
  const all = smart?.follow_up_tests || [];
  const matched = all.filter((t) => {
    const n = normalize(t.name);
    return sys.keywords.some((kw) => n.includes(kw));
  });
  return matched.length ? matched : all.slice(0, 2);
}

export default function InteractiveBodyMap({ smart, history = [], onJumpToBiomarkers }) {
  const [active, setActive] = useState(null);
  const [hover, setHover] = useState(null);
  const { platform } = useSiteSettings();
  const bodyStyle = platform?.body_diagram_style || "apple_minimal";
  const patientGender = String(smart?.patient_meta?.gender || "").toLowerCase();
  // Auto-select gender strictly from patient meta — no manual override.
  const effectiveGender = patientGender.startsWith("f") ? "female" : "male";

  const systems = useMemo(() => groupBySystem(smart?.biomarkers || []), [smart]);

  const activeItems = active ? systems[active] || [] : [];
  const activeSys = active ? SYSTEM_MAP[active] : null;
  const activeStatus = active ? worstStatus(activeItems) : null;
  const activeRecs = active ? recommendedTests(active, smart) : [];

  return (
    <div
      /* Responsive column ratios:
       *   ≥1280 (desktop):        32% / 68%
       *   1024-1279 (lg laptop):  35% / 65%
       *   640-1023 (tablet):      40% / 60%
       *   <640 (mobile):          stacked (single column)
       */
      className="grid grid-cols-1 sm:grid-cols-[40%_60%] lg:grid-cols-[35%_65%] xl:grid-cols-[32%_68%] gap-6 items-stretch print:hidden"
      data-testid="body-map"
      data-pdf-hidden="true"
    >
      <style>{`
        @keyframes bm-pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          50% { transform: translate(-50%, -50%) scale(1.12); opacity: 0.9; }
        }
        @keyframes bm-aura {
          0%, 100% { opacity: 0.35; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.15; transform: translate(-50%, -50%) scale(1.55); }
        }
        .bm-hotspot { animation: bm-pulse 2.6s ease-in-out infinite; }
        .bm-aura { animation: bm-aura 2.6s ease-in-out infinite; }
        .bm-detail-fade { animation: bmFadeIn 240ms ease-out both; }
        @keyframes bmFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: none; }
        }
      `}</style>

      {/* ═══ Body diagram card ═══ */}
      <div
        className="rounded-3xl bg-gradient-to-b from-slate-50 via-white to-slate-50 border border-border p-5 relative overflow-hidden flex flex-col"
        style={{ minHeight: 620, maxHeight: 780 }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-ink/50 font-heading font-semibold">Body Systems</div>
            <div className="text-[13px] text-ink/80 mt-0.5">
              {Object.keys(systems).length} of {Object.keys(SYSTEM_MAP).length} systems tested
            </div>
          </div>
          <div
            data-testid="body-gender-badge"
            className="text-[10.5px] rounded-full px-2.5 py-1 bg-white border border-border text-ink/70 font-medium"
            title="Diagram matches the patient's gender"
          >
            {effectiveGender === "female" ? "♀ Female" : "♂ Male"}
          </div>
        </div>
        <div className="relative w-full flex-1 mx-auto" style={{ maxWidth: "min(100%, 340px)" }}>
          <BodySilhouetteAnatomical gender={effectiveGender} style={bodyStyle} />

          {/* Hotspots */}
          {Object.entries(SYSTEM_MAP).map(([key, sys]) => {
            const items = systems[key] || [];
            const hasData = items.length > 0;
            const status = hasData ? worstStatus(items) : "not_tested";
            const [x, y] = sys.xy[effectiveGender];
            const isActive = active === key;
            const isHover = hover === key;
            const color = hasData ? statusColor(status) : "#CBD5E1";
            return (
              <React.Fragment key={key}>
                {/* Pulsing aura (only if this system has abnormal readings) */}
                {hasData && status !== "normal" && (
                  <span
                    className="bm-aura"
                    aria-hidden
                    style={{
                      position: "absolute",
                      left: `${x}%`,
                      top: `${y}%`,
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      background: color,
                      pointerEvents: "none",
                      zIndex: 5,
                    }}
                  />
                )}
                {/* Hotspot button */}
                <button
                  data-testid={`body-organ-${key}`}
                  onClick={() => hasData && setActive(key)}
                  onMouseEnter={() => setHover(key)}
                  onMouseLeave={() => setHover((h) => (h === key ? null : h))}
                  disabled={!hasData}
                  title={`${sys.label} — ${hasData ? items.length + " tests" : "not tested"}`}
                  className={`absolute transition-all duration-200 rounded-full grid place-items-center ${
                    hasData ? "hover:z-20 cursor-pointer" : "opacity-40 cursor-not-allowed"
                  } ${isActive ? "z-30" : ""}`}
                  style={{
                    left: `${x}%`,
                    top: `${y}%`,
                    width: isActive || isHover ? 34 : 26,
                    height: isActive || isHover ? 34 : 26,
                    background: `radial-gradient(circle at 30% 30%, ${lighten(color, 25)}, ${color})`,
                    border: "2.5px solid white",
                    boxShadow: isActive
                      ? `0 0 0 4px ${color}33, 0 8px 20px -4px ${color}66`
                      : "0 3px 8px rgba(0,0,0,0.18)",
                    transform: `translate(-50%, -50%) ${isActive || isHover ? "scale(1.06)" : "scale(1)"}`,
                    zIndex: isActive ? 30 : isHover ? 20 : 10,
                  }}
                >
                  {/* Icon appears on hover / active */}
                  <span
                    className="text-[14px] transition-opacity duration-200"
                    style={{ opacity: isHover || isActive ? 1 : 0 }}
                    aria-hidden
                  >
                    {sys.icon}
                  </span>
                  {/* Count fallback when not hovered */}
                  {!(isHover || isActive) && (
                    <span
                      className="absolute inset-0 grid place-items-center text-white text-[9px] font-bold"
                      aria-hidden
                    >
                      {items.length || ""}
                    </span>
                  )}
                </button>

                {/* Floating label chip — only visible on hover / active */}
                {(isHover || isActive) && hasData && (
                  <div
                    className="absolute pointer-events-none bm-detail-fade"
                    style={{
                      left: `${x}%`,
                      top: `${y}%`,
                      transform: `translate(${x > 50 ? "-115%" : "15%"}, -50%)`,
                      zIndex: 40,
                    }}
                  >
                    <div
                      className="rounded-full px-2.5 py-1 flex items-center gap-1.5 whitespace-nowrap"
                      style={{
                        background: "rgba(15, 23, 42, 0.94)",
                        color: "white",
                        boxShadow: "0 6px 20px rgba(15,23,42,0.35)",
                        fontSize: 11,
                      }}
                    >
                      <span>{sys.icon}</span>
                      <span className="font-semibold">{sys.label}</span>
                      <span
                        className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                        style={{ background: color, color: "white" }}
                      >
                        {items.length}
                      </span>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-4 grid grid-cols-2 gap-1.5 text-[10.5px] min-w-0">
          <LegendDot color="#22C55E" label="Normal" />
          <LegendDot color="#F59E0B" label="Watch" />
          <LegendDot color="#EF4444" label="Attention" />
          <LegendDot color="#CBD5E1" label="Not tested" />
        </div>
        <div className="mt-3 pt-3 border-t border-border/70 text-[10.5px] text-ink/50 flex items-center gap-1">
          <Info className="w-3 h-3" /> Tap any glowing dot to explore that system
        </div>
      </div>

      {/* ═══ Vitals panel ═══ */}
      <div
        className="rounded-3xl bg-white border border-border overflow-y-auto relative flex flex-col"
        style={{ minHeight: 620, maxHeight: 780 }}
        data-testid="body-detail-panel"
      >
        {!active && <EmptyState />}
        {active && activeSys && (
          <div className="bm-detail-fade">
            {/* Hero */}
            <div
              className="relative text-white px-6 pt-6 pb-8"
              style={{
                background: activeSys.gradientCss,
                backgroundImage: `radial-gradient(1200px circle at 100% 0%, rgba(255,255,255,0.35), transparent 40%), ${activeSys.gradientCss}`,
              }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div
                    className="w-16 h-16 rounded-2xl grid place-items-center text-3xl shadow-xl"
                    style={{ background: "rgba(255,255,255,0.2)", backdropFilter: "blur(8px)" }}
                  >
                    {activeSys.icon}
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-widest opacity-80 font-heading font-semibold">
                      System Health
                    </div>
                    <div className="font-heading font-bold text-2xl mt-0.5" data-testid="body-detail-title">
                      {activeSys.label}
                    </div>
                    <div className="mt-1 text-[12px] opacity-90">
                      {activeItems.length} parameter{activeItems.length === 1 ? "" : "s"} tested
                    </div>
                  </div>
                </div>
                <Button
                  data-testid="body-detail-close"
                  onClick={() => setActive(null)}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/15 shrink-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              {onJumpToBiomarkers && (
                <button
                  type="button"
                  data-testid="body-jump-to-biomarkers"
                  onClick={() => onJumpToBiomarkers({ system: active, label: activeSys.label, items: activeItems })}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur px-3.5 py-1.5 text-[11.5px] font-semibold text-white transition-colors"
                >
                  <span>View {activeItems.length} biomarker{activeItems.length === 1 ? "" : "s"} in table</span>
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Biomarker cards */}
              <div>
                <div className="text-[11px] uppercase tracking-widest text-ink/50 font-heading font-semibold mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> Parameters ({activeItems.length})
                </div>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {activeItems.map((b, i) => (
                    <VitalsBiomarkerCard key={i} b={b} history={history} />
                  ))}
                </div>
              </div>

              {/* AI Summary */}
              <div
                className="p-4 rounded-2xl relative overflow-hidden"
                style={{
                  background: `linear-gradient(135deg, ${activeSys.ring}12, ${activeSys.ring}03)`,
                  border: `1px solid ${activeSys.ring}30`,
                }}
              >
                <div className="text-[11px] uppercase tracking-widest text-ink/50 font-heading font-semibold flex items-center gap-1.5">
                  <TrendingUp className="w-3 h-3" style={{ color: activeSys.ring }} /> AI Insight
                </div>
                <p className="mt-1.5 text-[13px] text-ink/85 leading-relaxed">
                  {systemSummary(active, activeItems, activeStatus)}
                </p>
              </div>

              {/* Recommended tests */}
              {activeRecs.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-ink/50 font-heading font-semibold mb-2">
                    Recommended follow-up
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {activeRecs.map((t, i) => (
                      <div
                        key={i}
                        data-testid={`body-rec-${i}`}
                        className="group p-3 rounded-xl border border-border bg-white hover:border-ink/30 hover:shadow-sm transition-all cursor-pointer flex items-start gap-2"
                      >
                        <div
                          className="w-7 h-7 rounded-lg shrink-0 grid place-items-center text-white text-[11px] font-bold"
                          style={{ background: activeSys.ring }}
                        >
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-heading font-semibold text-ink text-[13px]">{t.name}</div>
                          {t.reason && (
                            <div className="text-[11px] text-ink/60 mt-0.5">{t.reason}</div>
                          )}
                          {t.frequency && (
                            <div className="text-[10px] font-medium mt-1" style={{ color: activeSys.ring }}>
                              {t.frequency}
                            </div>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-ink/30 group-hover:text-ink/60 transition-colors mt-0.5" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════ Sub-components ══════════════ */

function EmptyState() {
  return (
    <div className="h-full grid place-items-center text-center py-16 px-6">
      <div className="max-w-sm">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-slate-100 grid place-items-center mb-4">
          <span className="text-2xl">🫀</span>
        </div>
        <div className="font-heading font-bold text-ink text-lg">Tap any body system</div>
        <p className="text-sm text-ink/60 mt-1.5 leading-relaxed">
          Click a glowing dot on the body diagram to see related biomarkers,
          trends, and doctor-recommended follow-up tests for that system.
        </p>
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <span
        className="w-2.5 h-2.5 rounded-full shrink-0"
        style={{
          background: color,
          border: "1.5px solid white",
          boxShadow: "0 0 0 1px rgba(15,23,42,0.15)",
        }}
      />
      <span className="text-ink/70 truncate">{label}</span>
    </div>
  );
}


function VitalsBiomarkerCard({ b, history }) {
  const range = parseRange(b.reference_range);
  const val = parseFloat(b.value);
  const pos = rangePosition(val, range);
  const color = statusColor(b.status);

  // Historical sparkline
  const series = (history || [])
    .map((r) => {
      const match = (r.biomarkers || []).find((x) => x.name === b.name);
      if (!match) return null;
      const v = parseFloat(match.value);
      return isFinite(v) ? { value: v } : null;
    })
    .filter(Boolean);

  return (
    <div className="p-3 rounded-xl border border-border bg-white hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
            <span className="font-medium text-ink text-[12.5px] truncate">{b.name}</span>
            {b.flag && (
              <span
                className="text-[9px] font-bold px-1 py-0.5 rounded"
                style={{ background: `${color}20`, color }}
              >
                {b.flag}
              </span>
            )}
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="font-heading font-bold text-ink text-lg leading-none">{b.value}</span>
            <span className="text-[10px] text-ink/50">{b.unit}</span>
          </div>
          <div className="text-[10px] text-ink/50 mt-0.5">Ref: {b.reference_range}</div>
        </div>
        {series.length >= 2 && (
          <div className="w-14 h-8 shrink-0">
            <ResponsiveContainer>
              <LineChart data={series}>
                <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      {/* Range position bar */}
      {range && pos !== null && (
        <div className="mt-2 relative h-1.5 rounded-full bg-slate-100 overflow-hidden">
          {/* Normal zone shaded */}
          <div className="absolute inset-y-0 bg-emerald-100" style={{ left: "20%", right: "20%" }} />
          {/* Marker */}
          <div
            className="absolute -top-0.5 w-2.5 h-2.5 rounded-full"
            style={{
              left: `calc(${Math.max(2, Math.min(98, pos))}% - 5px)`,
              background: color,
              border: "1.5px solid white",
              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
            }}
          />
        </div>
      )}
    </div>
  );
}

function systemSummary(systemKey, items, worst) {
  const abnormal = items.filter((x) => x.status !== "normal");
  const sysName = SYSTEM_MAP[systemKey]?.label || "This system";
  if (worst === "normal") {
    return `${sysName} looks healthy — all ${items.length} related parameters are within their reference ranges. Keep up your current lifestyle and re-check yearly.`;
  }
  if (abnormal.length === 1) {
    const b = abnormal[0];
    return `${sysName} shows one parameter (${b.name}) outside the normal range. Consult your doctor and consider a follow-up test in 4-6 weeks.`;
  }
  return `${sysName} has ${abnormal.length} parameters outside the normal range. Consult your doctor for a personalized action plan. Diet, physical activity and follow-up testing can improve most of these values.`;
}

/* ══════════════ Utility ══════════════ */

/** Lighten a hex color by percent (0-100). */
function lighten(hex, percent) {
  const clean = hex.replace("#", "");
  const num = parseInt(clean, 16);
  const r = Math.min(255, ((num >> 16) & 255) + Math.round(255 * (percent / 100)));
  const g = Math.min(255, ((num >> 8) & 255) + Math.round(255 * (percent / 100)));
  const b = Math.min(255, (num & 255) + Math.round(255 * (percent / 100)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}
